import { palette } from '$tablet-app/src/app/theme';
import { showErrorToast } from '$tablet-app/src/app/toasts';
import { Button } from '@akin-home/buttons/native';
import React, { useEffect, useMemo, useState } from 'react';
import { Subscription } from 'react-native-ble-plx';
import { useRobotStateStore } from '../../stores/useRobotStateStore';
import { RobotDevice } from '../../types';
import { useRobotConnection } from '../../websockets/hooks/useRobotConnection';
import { isRobotAccessible } from '../functions';
import { BT_GATT_UUIDS } from '../types';
import { useBleConnection } from '../useBLEConnection';
import { ConnectToWifiModal } from './ConnectToWifiModal';

interface ConnectToRobotButtonProps {
  robotDevice: RobotDevice | undefined;
}

export const ConnectToRobotButton = (props: ConnectToRobotButtonProps) => {
  const { robotDevice } = props;

  const bluetoothDevice = useMemo(
    () => robotDevice?.bluetoothConnection?.bluetoothDevice,
    [robotDevice?.bluetoothConnection?.bluetoothDevice]
  );

  const establishBluetoothConnection = useRobotStateStore(
    (state) => state.establishRobotBluetoothConnection
  );
  const bleConn = useBleConnection(robotDevice?.id ?? '');

  const [showWifiModal, setShowWifiModal] = useState<boolean>(false);

  const establishWebsocketConnection = useRobotStateStore(
    (state) => state.establishRobotWebsocketConnection
  );
  const { disconnect, connectionStatus: connectionStatus2 } =
    useRobotConnection({ robotId: robotDevice?.id ?? '' });

  /**
   * Monitor if the currently connected device disconnects.
   * If it does, then we reset connection status regardless of what else is going on.
   */
  useEffect(() => {
    const sub = bluetoothDevice?.onDisconnected((error) => {
      console.log('Device disconnected due to', error);

      if (showWifiModal || connectionStatus2 === 'connecting') {
        showErrorToast({
          text1: 'Bluetooth Disconnection',
          text2: `The bluetooth connection to ${
            robotDevice?.name ?? 'the robot'
          } was lost. Please try again.`,
        });
      }

      setShowWifiModal(false);
    });

    return () => {
      sub?.remove();
    };
  }, [bluetoothDevice, connectionStatus2, robotDevice?.name, showWifiModal]);

  const disconnectAllConnections = async () => {
    await bleConn.disconnect();
    disconnect();
  };

  const handlePress = async () => {
    if (
      connectionStatus2 === 'connected' ||
      connectionStatus2 === 'connecting'
    ) {
      // √ NEXT: Close Connection if possible
      await disconnectAllConnections();
    } else {
      // If we do not have the robot Device we cannot do anything
      if (robotDevice === undefined) {
        return;
      }

      // If we do not have bluetooth or ip, we cannot do anything.
      if (
        bluetoothDevice === undefined &&
        robotDevice.websocketConnection?.ipAddress === undefined
      ) {
        return;
      }

      // If the IP of the robot is known, try to access it through that.
      if (robotDevice.websocketConnection?.ipAddress) {
        const host = robotDevice.websocketConnection?.ipAddress?.split(
          ':',
          1
        )?.[0];
        if (await isRobotAccessible(host)) {
          establishWebsocketConnection(robotDevice, `${host}:8000`);
          return;
        } else if (bluetoothDevice === undefined) {
          showErrorToast({
            text1: 'Robot Not Found',
            text2:
              'The robot was not found. Please try reconnecting to it via bluetooth instead.',
          });
        } else {
          // Do nothing, we continue on to attempt bluetooth.
        }
      }

      // If we have its bluetooth instead
      if (bluetoothDevice !== undefined) {
        // √ NEXT: Connect to Device
        const connectionSuccess = await establishBluetoothConnection(
          robotDevice,
          bluetoothDevice
        );
        if (!connectionSuccess) {
          return;
        }

        // √ NEXT: Check if robot already accessible
        let getIPError = false;
        const ipAddress = await bleConn.readCharacteristic(
          BT_GATT_UUIDS.SERVICE_AKIN_CONNECT,
          BT_GATT_UUIDS.CHAR_IP_ADDR,
          {
            onError: () => {
              getIPError = true;
            },
          }
        );
        if (getIPError) {
          // If there is an error, then we want an early return
          await disconnectAllConnections();
          return;
        }

        if (await isRobotAccessible(ipAddress)) {
          // NEXT: Connect to websocket
          if (robotDevice) {
            establishWebsocketConnection(robotDevice, `${ipAddress}:8000`);
          } else {
            console.log(
              "Robot id is not available. This shouldn't be happening."
            );
            await disconnectAllConnections();
          }
        } else {
          // √ NEXT: Open Modal
          setShowWifiModal(true);
        }
      }
    }
  };

  const handleModalClose = async (success: boolean) => {
    setShowWifiModal(false);

    if (!success) {
      // Handle error
      console.log('Connecting to robot stopped due to modal being closed');

      await disconnectAllConnections();
      return;
    }

    // √ NEXT: Check if the robot is now accesible
    let getIPError = false;
    const ipAddress = await bleConn.readCharacteristic(
      BT_GATT_UUIDS.SERVICE_AKIN_CONNECT,
      BT_GATT_UUIDS.CHAR_IP_ADDR,
      {
        onError: () => {
          getIPError = true;
        },
      }
    );
    if (getIPError) {
      // If there is an error, then we want an early return
      await disconnectAllConnections();
      return;
    }

    if (await isRobotAccessible(ipAddress)) {
      // NEXT: Connect to websocket
      if (robotDevice) {
        establishWebsocketConnection(robotDevice, `${ipAddress}:8000`);
      } else {
        console.log(
          "Robot device is not available. This shouldn't be happening."
        );
        await disconnectAllConnections();
      }
    } else {
      // √ NEXT: Disconnect from bluetooth if not success
      console.error('Robot not found');

      showErrorToast({
        text1: 'Something went wrong',
        text2: `${
          robotDevice?.name ?? 'The robot'
        } could not be found on your network. Please try again.`,
      });
      await disconnectAllConnections();
    }
  };

  const handleConnectingToWifi = async (
    ssid: string,
    password: string | undefined
  ): Promise<string | boolean> => {
    // √ NEXT: monitor characteristic
    const robotResponsePromise: Promise<string> = new Promise<{
      responseListener: Subscription | undefined;
      value: string;
    }>((resolve, reject) => {
      const responseListener = bleConn.monitorCharacteristic(
        BT_GATT_UUIDS.SERVICE_AKIN_CONNECT,
        BT_GATT_UUIDS.CHAR_ERROR,
        (error, value) => {
          if (error) {
            reject({ responseListener, error: error?.message });
            return;
          }

          if (value === undefined || value === null) {
            reject({
              responseListener,
              error: 'Received characteristic value is null',
            });
            return;
          }

          resolve({ responseListener, value });
        }
      );
    }).then(
      ({ responseListener, value }) => {
        responseListener?.remove();
        return value ?? '';
      },
      ({ responseListener, error }) => {
        responseListener?.remove();
        return error ?? '';
      }
    );

    // Note the use of shortcutting in the following for res
    // If writing the ssid fails, then the password is not attempted to be written at all.
    let res = true;
    res =
      res &&
      (await bleConn.writeCharacteristic(
        BT_GATT_UUIDS.SERVICE_AKIN_CONNECT,
        BT_GATT_UUIDS.CHAR_SSID,
        ssid
      ));

    res =
      res &&
      (await bleConn.writeCharacteristic(
        BT_GATT_UUIDS.SERVICE_AKIN_CONNECT,
        BT_GATT_UUIDS.CHAR_PASSWORD,
        password ?? ''
      ));
    if (!res) {
      return 'Could not send credentials to robot.';
    }

    try {
      const robotResponse = await robotResponsePromise;
      if (robotResponse?.toLowerCase() === 'success') {
        return true;
      } else {
        return robotResponse;
      }
    } catch (err) {
      console.log('Writing the ssid and/or password failed:', err);

      return (await bluetoothDevice?.isConnected())
        ? `${err}`
        : 'Bluetooth Disconnected';
    }
  };
  /******************
   * Render helpers *
   ******************/

  const shouldBeDisabled =
    robotDevice === undefined ||
    connectionStatus2 === 'connecting' ||
    connectionStatus2 === 'disconnecting';

  let buttonText = '';
  if (connectionStatus2 === 'connected') {
    buttonText = 'Disconnect';
  } else if (connectionStatus2 === 'connecting') {
    buttonText = 'Connecting';
  } else if (connectionStatus2 === 'disconnecting') {
    buttonText = 'Disconnecting';
  } else {
    buttonText = robotDevice?.name
      ? `Connect to ${robotDevice.name}`
      : `Connect`;
  }

  return (
    <>
      <Button
        size="large"
        onPress={handlePress}
        disabled={shouldBeDisabled}
        style={
          shouldBeDisabled && {
            backgroundColor: palette.dim,
          }
        }
        textStyle={{ textTransform: 'capitalize' }}
      >
        {buttonText}
      </Button>

      {showWifiModal && (
        <ConnectToWifiModal
          onClose={handleModalClose}
          connectToWifi={handleConnectingToWifi}
        />
      )}
    </>
  );
};
